import {
  ALLOWED_NATIVE_EXTENSIONS,
  ALLOWED_PDF_EXTENSIONS,
  DOCUMENT_INDICES,
  DOCUMENT_NATURE_OPTIONS,
  MAX_NATIVE_SIZE_BYTES,
  MAX_PDF_SIZE_BYTES,
} from "@etudescarre/constants";
import { Button, Select } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { combineDateAndTime, nowLocalTime, todayLocal } from "@/lib/utils/date";
import { useYardContext } from "@/providers/yard-context";
import { FieldError } from "../field-error";

// Enregistrer une pièce du marché initial (écran 001). Deux flux
// distincts : « ajouter un document seul » et « ajouter une maquette seule ».
// Le flux combiné « maquette + premiers documents » n'est PAS fourni ici
// (voir register-reception-form.tsx).

const IFC_EXTENSION = "ifc";

const inputClass =
  "w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100";
const labelClass =
  "mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100";
const fileInputClass =
  "w-full text-sm text-zinc-500 file:mr-4 file:rounded-md file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-blue-700 hover:file:bg-blue-100 dark:text-zinc-400 dark:file:bg-blue-900/20 dark:file:text-blue-400";

/** Reject a picked file that is not in the allow-list or over the size limit. */
export function pickFile(
  setter: (f: File | null) => void,
  allowed: readonly string[],
  maxSize: number,
  maxLabel: string,
) {
  return (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (isNil(file)) return;
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (isNil(ext) || !allowed.includes(ext)) {
      toaster.error({
        title: "Erreur",
        description: `Extension non autorisée. Acceptées : ${allowed.join(", ")}`,
      });
      e.target.value = "";
      return;
    }
    if (file.size > maxSize) {
      toaster.error({
        title: "Erreur",
        description: `Le fichier dépasse ${maxLabel}`,
      });
      e.target.value = "";
      return;
    }
    setter(file);
  };
}

/** Serialize a form body (metadata + files) into multipart/form-data. */
export function multipartSerializer(body: unknown) {
  const fd = new FormData();
  for (const [key, value] of Object.entries(body as Record<string, unknown>)) {
    if (value instanceof File) {
      fd.append(key, value, value.name);
    } else if (isNotNil(value)) {
      fd.append(key, String(value));
    }
  }
  return fd;
}

/** Every active contract of the yard: the author may be any participant. */
export function useYardAuthorOptions(yardId: number) {
  const [options, setOptions] = useState<{ label: string; value: string }[]>(
    [],
  );

  useEffect(() => {
    let cancelled = false;
    async function fetchContracts() {
      try {
        const { data, error } = await apiClient.GET(
          "/api/yards/{yardId}/contracts",
          { params: { path: { yardId: String(yardId) } } },
        );
        if (cancelled) return;
        if (error || isNil(data)) {
          toaster.error({
            title: "Erreur",
            description: "Impossible de charger les intervenants du chantier",
          });
          return;
        }
        setOptions(
          data.data.contracts
            .filter((c) => c.isActive)
            .map((c) => {
              const company = c.performerCompanyName ?? c.type;
              return {
                value: String(c.id),
                label: c.code ? `${company} (${c.code})` : company,
              };
            })
            .sort((a, b) => a.label.localeCompare(b.label)),
        );
      } catch (err) {
        console.error("Failed to load yard contracts", err);
      }
    }
    fetchContracts();
    return () => {
      cancelled = true;
    };
  }, [yardId]);

  return options;
}

function ContractField({
  value,
  onChange,
  error,
}: {
  value: number;
  onChange: (id: number) => void;
  error: string | null | undefined;
}) {
  const { yard } = useYardContext();
  const options = useYardAuthorOptions(yard.id);
  return (
    <div>
      <span className={labelClass}>
        Contrat auteur <span className="text-red-500">*</span>
      </span>
      <Select
        options={options}
        value={value > 0 ? [String(value)] : []}
        onValueChange={(details) => {
          const val = details.value[0];
          onChange(val ? Number(val) : 0);
        }}
        placeholder="Sélectionnez un contrat"
        required
      />
      <FieldError message={error} />
    </div>
  );
}

function FormActions({
  onCancel,
  submitting,
  submitLabel,
}: {
  onCancel: () => void;
  submitting: boolean;
  submitLabel: string;
}) {
  return (
    <div className="flex justify-end gap-3 pt-2">
      <Button type="button" outline onClick={onCancel} disabled={submitting}>
        Annuler
      </Button>
      <Button type="submit" disabled={submitting}>
        {submitting ? "En cours..." : submitLabel}
      </Button>
    </div>
  );
}

const MISSING_AUTHOR = "Sélectionnez le contrat auteur";

/** The required fields top the form, which may be scrolled down to its button. */
function showFirstError(
  event: React.FormEvent<HTMLFormElement>,
  invalid: Record<string, string | null>,
) {
  if (Object.values(invalid).some(isNotNil)) {
    event.currentTarget.scrollIntoView();
  }
}

type DocumentFormData = {
  authorContractId: number;
  number: string;
  name: string;
  nature: string;
  index: string;
};

export function DossierMarcheDocumentForm({
  onClose,
}: {
  onClose: () => void;
}) {
  const { yard } = useYardContext();
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [nativeFile, setNativeFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Fields are only flagged once the submit button was clicked.
  const [triedSubmit, setTriedSubmit] = useState(false);

  const form = useForm<DocumentFormData>({
    defaultValues: {
      authorContractId: 0,
      number: "",
      name: "",
      nature: "",
      index: "O",
    },
  });

  const authorContractId = form.watch("authorContractId");
  const number = form.watch("number");
  const name = form.watch("name");
  const nature = form.watch("nature");
  const index = form.watch("index");

  const invalid = {
    contract: authorContractId > 0 ? null : MISSING_AUTHOR,
    number: number.trim() ? null : "Renseignez le n° du document",
    name: name.trim() ? null : "Renseignez l'intitulé du document",
  };
  const errors: Partial<typeof invalid> = triedSubmit ? invalid : {};

  const onSubmit = form.handleSubmit(async (data) => {
    if (Object.values(invalid).some(isNotNil)) return;
    setSubmitting(true);
    try {
      const metadata = {
        authorContractId: data.authorContractId,
        number: data.number,
        name: data.name,
        index: data.index || "O",
        ...(data.nature ? { nature: data.nature } : {}),
      };
      const { error } = await apiClient.POST(
        "/api/yards/{yardId}/dossier-marche/documents",
        {
          params: { path: { yardId: String(yard.id) } },
          body: {
            metadata: JSON.stringify(metadata),
            pdf: pdfFile ?? undefined,
            native: nativeFile ?? undefined,
          },
          bodySerializer: multipartSerializer,
        },
      );
      if (error) {
        toaster.error({
          title: "Erreur",
          description: error.error ?? "Échec de l'enregistrement",
        });
        return;
      }
      toaster.success({ title: "Succès", description: "Document enregistré" });
      onClose();
    } catch {
      toaster.error({
        title: "Erreur",
        description: "Une erreur est survenue",
      });
    } finally {
      setSubmitting(false);
    }
  });

  return (
    // noValidate: the browser's bubble is in its own language and shows
    // nothing next to the field.
    <form
      noValidate
      onSubmit={(event) => {
        setTriedSubmit(true);
        showFirstError(event, invalid);
        void onSubmit(event);
      }}
      className="space-y-4"
    >
      <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
        Ajouter un document du marché initial
      </h3>

      <ContractField
        value={authorContractId}
        onChange={(id) => form.setValue("authorContractId", id)}
        error={errors.contract}
      />

      <div>
        <label htmlFor="docNumber" className={labelClass}>
          N° du document <span className="text-red-500">*</span>
        </label>
        <input
          id="docNumber"
          type="text"
          {...form.register("number")}
          required
          minLength={1}
          maxLength={255}
          className={inputClass}
        />
        <FieldError message={errors.number} />
      </div>

      <div>
        <label htmlFor="docName" className={labelClass}>
          Intitulé <span className="text-red-500">*</span>
        </label>
        <input
          id="docName"
          type="text"
          {...form.register("name")}
          required
          minLength={1}
          maxLength={255}
          className={inputClass}
        />
        <FieldError message={errors.name} />
      </div>

      <div>
        <span className={labelClass}>Nature</span>
        <Select
          options={DOCUMENT_NATURE_OPTIONS}
          value={nature ? [nature] : []}
          onValueChange={(details) =>
            form.setValue("nature", details.value[0] ?? "")
          }
          placeholder="Sélectionnez une nature"
        />
      </div>

      <div>
        <span className={labelClass}>Indice</span>
        <Select
          options={DOCUMENT_INDICES.map((idx) => ({ label: idx, value: idx }))}
          value={[index]}
          onValueChange={(details) =>
            form.setValue("index", details.value[0] ?? "O")
          }
          placeholder="Indice"
        />
      </div>

      <div>
        <label htmlFor="docPdf" className={labelClass}>
          Fichier PDF
        </label>
        <input
          id="docPdf"
          type="file"
          accept={ALLOWED_PDF_EXTENSIONS.map((e) => `.${e}`).join(",")}
          onChange={pickFile(
            setPdfFile,
            ALLOWED_PDF_EXTENSIONS,
            MAX_PDF_SIZE_BYTES,
            "50 Mo",
          )}
          className={fileInputClass}
        />
        {isNotNil(pdfFile) && (
          <p className="mt-1 text-xs text-zinc-500">{pdfFile.name}</p>
        )}
      </div>

      <div>
        <label htmlFor="docNative" className={labelClass}>
          Fichier natif
        </label>
        <input
          id="docNative"
          type="file"
          accept={ALLOWED_NATIVE_EXTENSIONS.map((e) => `.${e}`).join(",")}
          onChange={pickFile(
            setNativeFile,
            ALLOWED_NATIVE_EXTENSIONS,
            MAX_NATIVE_SIZE_BYTES,
            "200 Mo",
          )}
          className={fileInputClass}
        />
        {isNotNil(nativeFile) && (
          <p className="mt-1 text-xs text-zinc-500">{nativeFile.name}</p>
        )}
      </div>

      <FormActions
        onCancel={onClose}
        submitting={submitting}
        submitLabel="Enregistrer le document"
      />
    </form>
  );
}

type MaquetteFormData = {
  authorContractId: number;
  number: string;
  name: string;
  dateEtat: string;
  heureEtat: string;
};

export function DossierMarcheMaquetteForm({
  onClose,
}: {
  onClose: () => void;
}) {
  const { yard } = useYardContext();
  const [ifcFile, setIfcFile] = useState<File | null>(null);
  const [nativeFile, setNativeFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Fields are only flagged once the submit button was clicked.
  const [triedSubmit, setTriedSubmit] = useState(false);

  const form = useForm<MaquetteFormData>({
    defaultValues: {
      authorContractId: 0,
      number: "",
      name: "",
      dateEtat: todayLocal(),
      heureEtat: nowLocalTime(),
    },
  });

  const authorContractId = form.watch("authorContractId");
  const number = form.watch("number");
  const name = form.watch("name");
  const dateEtat = combineDateAndTime(
    form.watch("dateEtat"),
    form.watch("heureEtat"),
  );

  const invalid = {
    contract: authorContractId > 0 ? null : MISSING_AUTHOR,
    number: number.trim() ? null : "Renseignez le n° de la maquette",
    name: name.trim() ? null : "Renseignez l'intitulé de la maquette",
    dateEtat: isNil(dateEtat)
      ? "Renseignez une date et une heure d'état valides"
      : null,
  };
  const errors: Partial<typeof invalid> = triedSubmit ? invalid : {};

  const onSubmit = form.handleSubmit(async (data) => {
    if (Object.values(invalid).some(isNotNil)) return;
    setSubmitting(true);
    try {
      const metadata = {
        authorContractId: data.authorContractId,
        number: data.number,
        name: data.name,
        dateEtat,
      };
      const { error } = await apiClient.POST(
        "/api/yards/{yardId}/dossier-marche/maquettes",
        {
          params: { path: { yardId: String(yard.id) } },
          body: {
            metadata: JSON.stringify(metadata),
            ifc: ifcFile ?? undefined,
            native: nativeFile ?? undefined,
          },
          bodySerializer: multipartSerializer,
        },
      );
      if (error) {
        toaster.error({
          title: "Erreur",
          description: error.error ?? "Échec de l'enregistrement",
        });
        return;
      }
      toaster.success({ title: "Succès", description: "Maquette enregistrée" });
      onClose();
    } catch {
      toaster.error({
        title: "Erreur",
        description: "Une erreur est survenue",
      });
    } finally {
      setSubmitting(false);
    }
  });

  return (
    // noValidate: the browser's bubble is in its own language and shows
    // nothing next to the field.
    <form
      noValidate
      onSubmit={(event) => {
        setTriedSubmit(true);
        showFirstError(event, invalid);
        void onSubmit(event);
      }}
      className="space-y-4"
    >
      <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
        Ajouter une maquette du marché initial
      </h3>

      <ContractField
        value={authorContractId}
        onChange={(id) => form.setValue("authorContractId", id)}
        error={errors.contract}
      />

      <div>
        <label htmlFor="maqNumber" className={labelClass}>
          N° de la maquette <span className="text-red-500">*</span>
        </label>
        <input
          id="maqNumber"
          type="text"
          {...form.register("number")}
          required
          minLength={1}
          maxLength={255}
          className={inputClass}
        />
        <FieldError message={errors.number} />
      </div>

      <div>
        <label htmlFor="maqName" className={labelClass}>
          Intitulé <span className="text-red-500">*</span>
        </label>
        <input
          id="maqName"
          type="text"
          {...form.register("name")}
          required
          minLength={1}
          maxLength={255}
          className={inputClass}
        />
        <FieldError message={errors.name} />
      </div>

      <div className="flex gap-3">
        <div className="flex-1">
          <label htmlFor="maqDateEtat" className={labelClass}>
            Date d'état <span className="text-red-500">*</span>
          </label>
          <input
            id="maqDateEtat"
            type="date"
            {...form.register("dateEtat")}
            className={inputClass}
          />
        </div>
        <div className="flex-1">
          <label htmlFor="maqHeureEtat" className={labelClass}>
            Heure d'état <span className="text-red-500">*</span>
          </label>
          <input
            id="maqHeureEtat"
            type="time"
            {...form.register("heureEtat")}
            className={inputClass}
          />
        </div>
      </div>
      <FieldError message={errors.dateEtat} />

      <div>
        <label htmlFor="maqIfc" className={labelClass}>
          Fichier IFC
        </label>
        <input
          id="maqIfc"
          type="file"
          accept={`.${IFC_EXTENSION}`}
          onChange={pickFile(
            setIfcFile,
            [IFC_EXTENSION],
            MAX_NATIVE_SIZE_BYTES,
            "200 Mo",
          )}
          className={fileInputClass}
        />
        {isNotNil(ifcFile) && (
          <p className="mt-1 text-xs text-zinc-500">{ifcFile.name}</p>
        )}
      </div>

      <div>
        <label htmlFor="maqNative" className={labelClass}>
          Fichier natif
        </label>
        <input
          id="maqNative"
          type="file"
          accept={ALLOWED_NATIVE_EXTENSIONS.map((e) => `.${e}`).join(",")}
          onChange={pickFile(
            setNativeFile,
            ALLOWED_NATIVE_EXTENSIONS,
            MAX_NATIVE_SIZE_BYTES,
            "200 Mo",
          )}
          className={fileInputClass}
        />
        {isNotNil(nativeFile) && (
          <p className="mt-1 text-xs text-zinc-500">{nativeFile.name}</p>
        )}
      </div>

      <FormActions
        onCancel={onClose}
        submitting={submitting}
        submitLabel="Enregistrer la maquette"
      />
    </form>
  );
}
